import fileUtils from '../../../global/utils/fileUtils.js'
import {
  VALID_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
} from '../constants/fileConstants.js'

/**
 * Merges request template documents with request task documents
 * @param {Object} requestTemplate - The request template object
 * @param {Object} requestTask - The request task object
 * @returns {Array} Merged documents array
 */
export const mergeDocuments = (requestTemplate, requestTask) => {
  if (!requestTemplate?.documents) return requestTask.documents || []

  const taskDocs = requestTask.documents || []
  const templateDocsById = new Map(
    requestTemplate.documents.map(d => [d.id, d]),
  )

  const merged = taskDocs
    .filter(taskDoc => taskDoc.request_template_document_id)
    .map(taskDoc => {
      const templateDoc = templateDocsById.get(
        taskDoc.request_template_document_id,
      )
      return {
        ...templateDoc,
        ...taskDoc,
        files: taskDoc.files || [],
        id: taskDoc.id,
      }
    })
    .sort((a, b) => a.id - b.id)

  const customDocs = taskDocs.filter(d => !d.request_template_document_id)

  return [...merged, ...customDocs]
}

/**
 * Formats a date string to a readable format
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Validates a file for upload
 * @param {File} file - The file to validate
 * @returns {Object} Validation result with isValidFile and error properties
 */
export const validateFile = file => {
  const extension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf('.'))
  const isValidExtension = VALID_FILE_EXTENSIONS.includes(extension)
  const isValidName = fileUtils.isValidFilename(file.name)
  const isValidSize = file.size <= MAX_FILE_SIZE

  return {
    isValidFile: isValidExtension && isValidSize && isValidName,
    error: !isValidExtension
      ? 'Invalid file type'
      : !isValidSize
        ? 'File too large'
        : !isValidName
          ? 'Invalid file name'
          : null,
  }
}

/**
 * Generates a random ID for files
 * @returns {string} Random file ID
 */
export const generateRandomId = () =>
  `file_${Math.random().toString(36).substr(2, 9)}`
